import { DisplayTrixContent } from "@circle-react-uikit/DisplayTrixContent";
import type { LineClampLevel } from "@circle-react-uikit/SeeMoreLess";
import { SeeMoreLess } from "@circle-react-uikit/SeeMoreLess";
import { TipTapBlockEditor } from "@circle-react-uikit/TipTapBlockEditor";
import { useCurrentPostHelpers } from "@circle-react/components/PostsV3/useCurrentPostHelpers";
import { useCurrentPostContext } from "@circle-react/contexts/currentPostContext";
import { useSmScreenMediaQuery } from "@circle-react/hooks/useMediaQuery";
import { useBackendProps } from "@circle-react/providers/AppContext";
import InnerHTML from "dangerously-set-html-content";

import { t } from "@/i18n-js/instance";
import { anchorClassNames } from "@/react/helpers/cssAnchorHelpers";
import { isEventPost, isImagePost } from "@/react/helpers/postHelpers";
import { classNames } from "@/react/helpers/twMergeWithCN";
import { posts } from "@/react/helpers/urlHelpers";

import { useNavigateToPost } from "../PostModal/hooks/useNavigateToPost";
import { usePostNavigationContext } from "../PostViewList/PostNavigationContext";
import { MinimalTiptapContent } from "./MinimalTipTapContent";

export const PostBody = ({ enableTruncation = false, noPadding = false }) => {
  const {
    post,
    isSharedPostWithTeaserLayout,
    isSharedPostViewWithMinimalLayout,
    isTiptapPost,
    isTrixPost,
    isPostBodyEmpty,
  } = useCurrentPostHelpers();
  const isSmScreen = useSmScreenMediaQuery();
  const { navigateToPost } = useNavigateToPost();
  const { params, hasNextPage } = usePostNavigationContext();
  const { minimal_layout: isMinimalLayout } = useBackendProps();
  const { usedIn, isUsedInMinimalTipTapViewModal } = useCurrentPostContext();

  const isDisabled = !enableTruncation;

  const isMinimalTeaser =
    isSharedPostViewWithMinimalLayout && isSharedPostWithTeaserLayout;

  const shouldHideToggle = isSharedPostWithTeaserLayout;

  const shouldRenderCustomSeeMoreButton =
    !isUsedInMinimalTipTapViewModal && isEventPost(post) && !isDisabled;

  const {
    body_trix_content: bodyTrixContent,
    tiptap_body: tiptapBody,
    internal_custom_html: html,
    pinned_at_top_of_space: isPinned,
    updated_at: updatedAt,
  } = post;

  const shouldRenderTipTapTeaserPreview =
    isSharedPostWithTeaserLayout &&
    isTiptapPost &&
    Boolean(tiptapBody?.body?.content?.length);

  if (isPostBodyEmpty) {
    return null;
  }

  let lines: LineClampLevel = 8;
  if (isPinned) {
    lines = 2;
  } else if (isMinimalTeaser) {
    lines = 4;
  }

  return (
    <div
      key={updatedAt}
      className={classNames(anchorClassNames("post__body"), {
        "!p-0": noPadding,
        "px-5 pb-5 md:px-6":
          isImagePost(post) && !isUsedInMinimalTipTapViewModal,
        "mt-5":
          usedIn === "minimal-post-modal" &&
          (!post.hide_meta_info || (isSmScreen && post.name)),
      })}
      data-testid="post-body"
    >
      <div
        className={classNames(
          anchorClassNames("post__inside trix-v2"),
          "w-full max-lg:overflow-x-scroll",
        )}
        data-testid="post-body-inside"
      >
        {shouldRenderTipTapTeaserPreview ? (
          <MinimalTiptapContent hideSeeMoreButton />
        ) : (
          <SeeMoreLess
            lines={lines}
            disabled={isDisabled}
            hideToggle={shouldHideToggle || shouldRenderCustomSeeMoreButton}
          >
            {isTrixPost && bodyTrixContent && (
              <DisplayTrixContent
                content={bodyTrixContent}
                contentParentPath={posts.show({
                  slug: post.slug,
                  spaceSlug: post.space_slug,
                })}
              />
            )}
            {isTiptapPost && tiptapBody && (
              <TipTapBlockEditor
                readOnly
                disableMentionClicks={isMinimalLayout}
                value={tiptapBody.body}
                sgidToObjectMap={tiptapBody.sgids_to_object_map}
                inlineAttachments={tiptapBody.inline_attachments}
              />
            )}
            {html && <InnerHTML key="internal_custom_html" html={html} />}
          </SeeMoreLess>
        )}
        {shouldRenderCustomSeeMoreButton && (
          <button
            type="button"
            className="mt-2 text-md text-default hover:text-dark"
            onClick={() => {
              navigateToPost({
                newPost: post,
                params,
                hasNextPage,
              });
            }}
          >
            {t("see_more")}
          </button>
        )}
      </div>
    </div>
  );
};
